<script setup lang="ts">
type Props = {
  item: TStoreItem;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'select-item', item: TStoreItem): void;
  (e: 'open-details', item: TStoreItem): void;
}>();

function parseNumericVal(v: any): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    const match = s.match(/-?\d+(?:[.,]\d+)?/);

    if (!match) return undefined;

    const normalized = match[0].replace(/\./g, '').replace(',', '.');
    const n = Number(normalized);

    return Number.isFinite(n) ? n : undefined;
  }

  return undefined;
}

const originalPrice = computed<number | undefined>(() => {
  const val = (props.item as any)?.purchase_points_amount ?? props.item.price;

  return typeof val === 'number' ? val : undefined;
});

const discountedPrice = computed<number | undefined>(() => {
  const raw = (props.item as any)?.discounted_price;

  return parseNumericVal(raw);
});

const hasDiscount = computed(
  () =>
    typeof discountedPrice.value === 'number' &&
    (discountedPrice.value as number) > 0,
);

type TCurrency = 'points' | 'diamonds' | 'gems';

const itemCurrency = computed<TCurrency>(() => {
  const pt = String((props.item as any)?.purchase_type || '').toLowerCase();

  if (pt === 'points' || pt === 'diamonds' || pt === 'gems') {
    return pt as TCurrency;
  }

  const st = String((props.item as any)?.spanType || '').toLowerCase();

  if (
    typeof (props.item as any)?.price_diamonds === 'number' ||
    st.includes('diamonds')
  ) {
    return 'diamonds';
  }
  if (
    typeof (props.item as any)?.price_gems === 'number' ||
    st.includes('gem')
  ) {
    return 'gems';
  }

  return 'points';
});

const currencyIcon = computed(() => {
  if (itemCurrency.value === 'diamonds') return 'currency-header/diamond';
  if (itemCurrency.value === 'gems') return 'currency-header/gem';

  return 'currency-header/coin';
});

const currencyLabel = computed(() => {
  if (itemCurrency.value === 'diamonds') return 'DIAMONDS';
  if (itemCurrency.value === 'gems') return 'GEMS';

  return 'COINS';
});

// Cores por tipo de moeda
const currencyColors = computed(() => {
  if (itemCurrency.value === 'diamonds') {
    return {
      priceBackground: '#00C9FF1A',
      priceText: '#00C9FF',
      cardBackground:
        'linear-gradient(180deg, rgba(2, 10, 31, 0.46) 0%, rgba(8, 44, 133, 0.46) 100%)',
      borderGradient: 'linear-gradient(180deg, #1D2329 0%, #1BE0F8 100%)',
    };
  }

  if (itemCurrency.value === 'gems') {
    return {
      priceBackground: '#00FF401A',
      priceText: '#81FFA1',
      cardBackground:
        'linear-gradient(180deg, rgba(0, 43, 11, 0.20) 0%, rgba(0, 255, 64, 0.20) 100%)',
      borderGradient: 'linear-gradient(180deg, #1D2329 0%, #83F539 100%)',
    };
  }

  // COINS (padrão laranja)
  return {
    priceBackground: '#F66A0066',
    priceText: '#FF9B50',
    cardBackground:
      'linear-gradient(180deg, rgba(27, 7, 1, 0.40) 0%, rgba(129, 33, 4, 0.40) 100%)',
    borderGradient: 'linear-gradient(180deg, #1D2329 0%, #F66A00 100%)',
  };
});

const ribbonText = computed<string | null>(() => {
  const r = String((props.item as any)?.ribbon ?? '').trim();

  return r || null;
});

const customRibbonImage = computed<string | null>(() => {
  const img = String((props.item as any)?.custom_ribbon_image ?? '').trim();

  return img || null;
});

// Prioriza custom_ribbon_image, depois ribbon
const displayRibbon = computed<string | null>(() => {
  return customRibbonImage.value || ribbonText.value;
});

const canBuy = computed(() => {
  const pool = (props.item as any)?.pool;
  const poolOk = typeof pool !== 'number' || pool > 0;

  return !!props.item.can_buy && poolOk;
});

function handleBuy() {
  emit('select-item', props.item);
}
</script>

<template>
  <!-- Wrapper externo para dar espaço para a ribbon -->
  <div class="card-outer-wrapper">
    <!-- Card no estilo vertical conforme Figma -->
    <div
      class="card-wrapper relative backdrop-blur-[10px] inline-flex flex-col justify-between items-center select-none"
      :style="{
        background: currencyColors.cardBackground,
        position: 'relative',
        '--border-gradient': currencyColors.borderGradient,
      }"
      @click="handleBuy"
    >
      <!-- Ribbon no topo centralizado (prioriza custom_ribbon_image, depois ribbon) -->
      <div
        v-if="displayRibbon"
        class="ribbon-container"
      >
        <div
          class="text-center text-white text-[10.22px] font-semibold leading-[8.18px] whitespace-nowrap"
        >
          {{ displayRibbon }}
        </div>
      </div>

      <!-- Imagem -->
      <SmarticoApostatudoSmarticoImage
        :alt="props.item.name"
        class="w-20 h-20 sm:w-28 sm:h-28 rounded-md sm:rounded-lg cursor-pointer select-none"
        height="80"
        object-fit="cover"
        role="button"
        :src="props.item.image"
        tabindex="0"
        width="80"
        @click="emit('open-details', props.item)"
        @keyup.enter="emit('open-details', props.item)"
      />

      <!-- Título -->
      <div
        class="self-stretch justify-start text-white font-bold leading-[14px] sm:leading-[18px] line-clamp-2 cursor-pointer text-xs sm:text-base"
        role="button"
        tabindex="0"
        :title="props.item.name"
        @click="emit('open-details', props.item)"
        @keyup.enter="emit('open-details', props.item)"
      >
        {{ props.item.name }}
      </div>

      <!-- Descrição -->
      <div
        class="self-stretch justify-start text-white/50 text-[10px] sm:text-sm font-medium leading-[12px] sm:leading-[16px] line-clamp-2 cursor-pointer mt-1"
        role="button"
        tabindex="0"
        :title="props.item.description"
        @click="emit('open-details', props.item)"
        @keyup.enter="emit('open-details', props.item)"
      >
        {{ props.item.description }}
      </div>

      <!-- Bloco inferior: Preço + Botão -->
      <div
        class="self-stretch inline-flex justify-center items-center gap-[5px] sm:gap-1.5"
      >
        <!-- Preço -->
        <div
          class="flex-1 px-1.5 py-1 sm:px-2.5 sm:py-1 rounded-md sm:rounded-lg backdrop-blur-[6.80px] sm:backdrop-blur-[10px] flex justify-center items-center gap-1.5 sm:gap-2.5"
          :style="{ backgroundColor: currencyColors.priceBackground }"
        >
          <div class="flex justify-center items-center gap-1 sm:gap-2">
            <NuxtIcon
              class="text-[20px] sm:text-[26px]"
              filled
              :name="currencyIcon"
            />
            <div
              :class="[
                'inline-flex flex-col justify-center items-center gap-0',
                hasDiscount
                  ? '-mt-1.5 sm:-mt-2'
                  : 'justify-center mt-0 sm:mt-0',
              ]"
            >
              <!-- PREÇO COM DESCONTO (corte diagonal grosso) -->
              <template v-if="hasDiscount">
                <span class="price-cut">
                  <span class="price-cut__old">
                    {{ discountedPrice?.toLocaleString('pt-BR') }}
                  </span>
                </span>
              </template>

              <!-- Preço atual com label -->
              <div
                class="flex items-baseline justify-center gap-0.5 whitespace-nowrap"
              >
                <span
                  class="text-[10px] sm:text-sm font-semibold leading-[10px] sm:leading-[14px]"
                  :style="{ color: currencyColors.priceText }"
                >
                  {{ originalPrice?.toLocaleString('pt-BR') || '0' }}
                </span>
                <span
                  class="text-[7px] sm:text-[9px] font-extrabold leading-[7px] sm:leading-[10px]"
                  :style="{ color: currencyColors.priceText }"
                >
                  {{ currencyLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Botão de compra -->
        <div
          v-if="canBuy"
          class="w-8 sm:w-10 self-stretch px-[5.11px] sm:px-1.5 bg-[radial-gradient(ellipse_138.00%_168.30%_at_36.76%_-16.67%,_#FF704A_0%,_#FF3400_100%)] rounded-[3.07px] sm:rounded outline outline-[0.05px] sm:outline-[0.2px] outline-offset-[-0.05px] sm:outline-offset-[-0.2px] outline-white/50 inline-flex flex-col justify-center items-center gap-1 sm:gap-[4.77px] overflow-hidden cursor-pointer hover:brightness-90 transition"
          @click="handleBuy"
        >
          <NuxtIcon
            class="text-[16px] sm:text-[20px] text-white"
            filled
            name="store/cart2"
          />
        </div>

        <!-- Botão indisponível -->
        <div
          v-else
          class="w-8 sm:w-10 self-stretch px-[5.11px] sm:px-1.5 bg-neutral-700/40 rounded-[3.07px] sm:rounded outline outline-[0.51px] sm:outline-[0.68px] outline-offset-[-0.51px] sm:outline-offset-[-0.68px] outline-neutral-600 inline-flex flex-col justify-center items-center gap-1 sm:gap-[4.77px] overflow-hidden cursor-not-allowed opacity-60"
        >
          <NuxtIcon
            class="text-[16px] sm:text-[20px] text-white/50"
            filled
            name="lock_key"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Wrapper externo para dar espaço para a ribbon */
.card-outer-wrapper {
  display: inline-block;
  padding-top: 12px; /* espaço para metade da ribbon (height 20px / 2 = 10px + margem) */
  overflow: visible;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.price-cut {
  position: relative;
  display: inline-block;
}

.price-cut__old {
  color: white;
  font-size: 9px;
  font-weight: 600;
  line-height: 9px;
}

@media (min-width: 640px) {
  .price-cut__old {
    font-size: 12px;
    line-height: 14px;
  }
}

.price-cut::after {
  content: '';
  position: absolute;
  left: -2px;
  right: -2px;
  top: 50%;
  height: 1px;
  background: #ff3400;
  transform: translateY(-50%) rotate(-15deg);
  transform-origin: center;
}

@media (min-width: 640px) {
  .price-cut::after {
    height: 2px;
  }
}

.card-wrapper {
  /* Mobile: 160px x 200px */
  width: 160px;
  height: 200px;
  padding: 10px;
  border-radius: 6.78px;
}

/* Desktop: 213.17px x 266.46px */
@media (min-width: 640px) {
  .card-wrapper {
    width: 208px;
    height: 266.46px;
    padding: 13.32px;
    border-radius: 9.03px;
  }
}

.card-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  padding: 0.68px;
  background: var(--border-gradient);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: -1;
}

@media (min-width: 640px) {
  .card-wrapper::before {
    padding: 0.9px;
  }
}

/* Ribbon no topo centralizado - metade pra cima, metade pra baixo */
.ribbon-container {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 96px; /* w-24 - mínimo */
  width: auto; /* ajusta automaticamente ao conteúdo */
  height: 20px; /* h-5 */
  padding: 0 6px; /* px-1.5 */
  background: radial-gradient(
    ellipse 138% 168.3% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  border-radius: 4px;
  outline: 0.68px solid rgb(252 165 165); /* outline-red-300 */
  outline-offset: -0.68px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 4.77px;
  pointer-events: none;
}

/* Fix para iPhone 11: menos saturação no verde do preço das gems */
@supports (-webkit-touch-callout: none) {
  .card-wrapper {
    /* Reduz saturação do verde em dispositivos iOS */
    filter: saturate(0.95);
  }
}

@media only screen and (max-width: 428px) and (-webkit-min-device-pixel-ratio: 2) {
  .card-wrapper [style*='#00FF401A'],
  .card-wrapper [style*='background-color: rgb(0, 255, 64)'] {
    background-color: rgba(30, 122, 58, 0.3) !important;
  }
  .card-wrapper [style*='#81FFA1'],
  .card-wrapper [style*='color: rgb(129, 255, 161)'] {
    color: #70d98f !important;
  }
}
</style>
